import { z } from "zod";

/**
 * Interactive Lesson block schema.
 *
 * A lesson is an ordered list of blocks. The student sees one block at a time.
 * "Check" blocks (question, sort, order, match, cloze, tapword, numberline,
 * fraction) are scored; the rest are explanatory.
 *
 * Text fields marked "rich" accept a tiny markdown subset rendered by
 * client/src/components/lesson/RichText.tsx:
 *   **bold**, *italic*, `code`, line breaks (\n), bullet lines starting with "- ",
 *   and inline KaTeX between $...$ (write money as \$ so it is not parsed as math).
 */

const rich = z.string().min(1).max(4000);
const short = z.string().min(1).max(300);
const blockId = z.string().min(1).max(64);

// ---------- Visuals used inside explain blocks ----------

export const tableVisual = z.object({
  kind: z.literal("table"),
  headers: z.array(z.string().max(60)).min(1).max(6),
  rows: z.array(z.array(z.string().max(120)).min(1).max(6)).min(1).max(10),
});

export const emojiGridVisual = z.object({
  kind: z.literal("emojiGrid"),
  // Each row is a string of emoji/characters shown in a big font, e.g. "🍎🍎🍎"
  rows: z.array(z.string().min(1).max(60)).min(1).max(8),
  caption: short.optional(),
});

export const fractionVisual = z.object({
  kind: z.literal("fraction"),
  parts: z.number().int().min(2).max(16),
  shaded: z.number().int().min(0).max(16),
  shape: z.enum(["bar", "circle", "grid"]).default("bar"),
  caption: short.optional(),
});

export const numberLineVisual = z.object({
  kind: z.literal("numberLine"),
  min: z.number(),
  max: z.number(),
  step: z.number().positive().default(1),
  marks: z.array(z.object({ value: z.number(), label: z.string().max(20).optional() })).max(12).optional(),
  caption: short.optional(),
});

export const visualSchema = z.discriminatedUnion("kind", [
  tableVisual,
  emojiGridVisual,
  fractionVisual,
  numberLineVisual,
]);

export const calloutSchema = z.object({
  kind: z.enum(["tip", "remember", "funfact", "jamaica", "warning"]),
  text: rich,
});

// ---------- Blocks ----------

export const introBlock = z.object({
  type: z.literal("intro"),
  id: blockId,
  title: short,
  text: rich, // 1-3 short sentences spoken by the mascot
  emoji: z.string().max(8).optional(),
  goals: z.array(short).max(4).optional(),
});

export const explainBlock = z.object({
  type: z.literal("explain"),
  id: blockId,
  heading: short,
  body: rich,
  imageUrl: z.string().url().optional(),
  imageAlt: short.optional(),
  visual: visualSchema.optional(),
  callout: calloutSchema.optional(),
});

export const exampleBlock = z.object({
  type: z.literal("example"),
  id: blockId,
  heading: short,
  problem: rich,
  steps: z.array(z.object({ text: rich, note: short.optional() })).min(1).max(8),
  answer: rich.optional(),
});

export const questionOption = z.object({
  id: z.string().min(1).max(8), // "a", "b", "c", "d"
  text: z.string().min(1).max(300),
});

export const questionBlock = z.object({
  type: z.literal("question"),
  id: blockId,
  kind: z.enum(["mc", "multi", "tf", "numeric", "text"]),
  prompt: rich,
  imageUrl: z.string().url().optional(),
  options: z.array(questionOption).min(2).max(6).optional(), // mc / multi
  // mc: option id; multi: option ids; tf: "true" | "false"; numeric: number; text: accepted string(s)
  answer: z.union([z.string(), z.array(z.string()).min(1), z.number()]),
  tolerance: z.number().nonnegative().optional(), // numeric only
  hint: short.optional(),
  feedbackCorrect: short.optional(),
  feedbackWrong: short.optional(),
  explanation: rich.optional(),
});

export const sortBlock = z.object({
  type: z.literal("sort"),
  id: blockId,
  prompt: rich,
  bins: z.array(z.object({ id: z.string().min(1).max(32), label: short, emoji: z.string().max(8).optional() })).min(2).max(4),
  items: z.array(z.object({ id: z.string().min(1).max(32), text: z.string().min(1).max(120), bin: z.string().min(1).max(32) })).min(3).max(10),
});

export const orderBlock = z.object({
  type: z.literal("order"),
  id: blockId,
  prompt: rich,
  // Items listed in the CORRECT order; the player shuffles them.
  items: z.array(z.object({ id: z.string().min(1).max(32), text: z.string().min(1).max(160) })).min(3).max(7),
});

export const matchBlock = z.object({
  type: z.literal("match"),
  id: blockId,
  prompt: rich,
  pairs: z.array(z.object({ id: z.string().min(1).max(32), left: z.string().min(1).max(120), right: z.string().min(1).max(120) })).min(2).max(6),
});

export const clozeBlock = z.object({
  type: z.literal("cloze"),
  id: blockId,
  prompt: short.optional(),
  // Blanks are written as [[answer]]. Example: "Water boils at [[100]] degrees."
  text: z.string().min(1).max(1200),
  distractors: z.array(z.string().min(1).max(40)).max(6).optional(),
});

export const tapWordBlock = z.object({
  type: z.literal("tapword"),
  id: blockId,
  prompt: short, // e.g. "Tap every noun in the sentence."
  sentence: z.string().min(1).max(400),
  // Words to tap. Matched case-insensitively with punctuation stripped; every occurrence counts.
  targets: z.array(z.string().min(1).max(40)).min(1).max(10),
});

export const numberLineBlock = z.object({
  type: z.literal("numberline"),
  id: blockId,
  prompt: rich,
  min: z.number(),
  max: z.number(),
  step: z.number().positive(),
  target: z.number(),
  tolerance: z.number().nonnegative().optional(),
  labelEvery: z.number().positive().optional(), // label marks every N units (default: auto)
  hint: short.optional(),
  explanation: rich.optional(),
});

export const fractionBlock = z.object({
  type: z.literal("fraction"),
  id: blockId,
  prompt: rich,
  parts: z.number().int().min(2).max(16),
  target: z.number().int().min(0).max(16), // number of parts that should be shaded
  shape: z.enum(["bar", "circle", "grid"]).default("bar"),
  hint: short.optional(),
  explanation: rich.optional(),
});

/**
 * A live physics simulation the student drives with sliders.
 *
 * Built for CSEC Physics and for a phone. Every model below is computed natively in the renderer
 * from named inputs; there is no formula string and nothing is evaluated, so a lesson author
 * cannot inject code and a wrong formula cannot reach a student through content.
 *
 * The point is not decoration. A student who drags the launch angle and watches the range peak at
 * 45 degrees has learned something a paragraph cannot teach, and the challenge field turns that
 * into a graded step: reach this readout, within this tolerance, by finding the right inputs.
 */
export const SIM_MODELS = [
  "projectile",   // u, angle -> range, max height, time of flight     (Section A: Mechanics)
  "ohms-law",     // V, R -> I, P                                       (Section D: Electricity)
  "resistors",    // R1, R2, arrangement -> total resistance            (Section D)
  "pendulum",     // length, g -> period, frequency                     (Section A)
  "refraction",   // n1, n2, angle -> refracted angle, critical angle   (Section C: Waves and Optics)
  "wave",         // frequency, wavelength -> speed, period             (Section C)
] as const;

export const simulationBlock = z.object({
  type: z.literal("simulation"),
  id: blockId,
  prompt: rich,
  model: z.enum(SIM_MODELS),
  /** Sliders. `key` must be an input the chosen model understands. */
  controls: z.array(z.object({
    key: z.string().min(1).max(24),
    label: short,
    min: z.number(),
    max: z.number(),
    step: z.number().positive(),
    initial: z.number(),
    unit: z.string().max(12).optional(),
  })).min(1).max(4),
  /** Values shown live as the student drags. `key` must be a model output. */
  readouts: z.array(z.object({
    key: z.string().min(1).max(24),
    label: short,
    unit: z.string().max(12).optional(),
    decimals: z.number().int().min(0).max(3).default(1),
  })).min(1).max(4),
  /** Optional graded step: hit a target readout by choosing the right inputs. */
  challenge: z.object({
    text: rich,
    readout: z.string().min(1).max(24),
    target: z.number(),
    tolerance: z.number().nonnegative(),
  }).optional(),
  hint: short.optional(),
  explanation: rich.optional(),
});

export const flashcardsBlock = z.object({
  type: z.literal("flashcards"),
  id: blockId,
  heading: short.optional(),
  cards: z.array(z.object({ front: z.string().min(1).max(200), back: z.string().min(1).max(400), emoji: z.string().max(8).optional() })).min(2).max(10),
});

export const summaryBlock = z.object({
  type: z.literal("summary"),
  id: blockId,
  heading: short.optional(),
  points: z.array(rich).min(1).max(8),
  badgeText: short.optional(), // e.g. "Fraction Explorer"
});

export const lessonBlockSchema = z.discriminatedUnion("type", [
  simulationBlock,
  introBlock,
  explainBlock,
  exampleBlock,
  questionBlock,
  sortBlock,
  orderBlock,
  matchBlock,
  clozeBlock,
  tapWordBlock,
  numberLineBlock,
  fractionBlock,
  flashcardsBlock,
  summaryBlock,
]);

export type LessonBlock = z.infer<typeof lessonBlockSchema>;
export type LessonBlockType = LessonBlock["type"];
export type IntroBlock = z.infer<typeof introBlock>;
export type ExplainBlock = z.infer<typeof explainBlock>;
export type ExampleBlock = z.infer<typeof exampleBlock>;
export type QuestionBlock = z.infer<typeof questionBlock>;
export type SortBlock = z.infer<typeof sortBlock>;
export type OrderBlock = z.infer<typeof orderBlock>;
export type MatchBlock = z.infer<typeof matchBlock>;
export type ClozeBlock = z.infer<typeof clozeBlock>;
export type TapWordBlock = z.infer<typeof tapWordBlock>;
export type NumberLineBlock = z.infer<typeof numberLineBlock>;
export type SimulationBlock = z.infer<typeof simulationBlock>;
export type FractionBlock = z.infer<typeof fractionBlock>;
export type FlashcardsBlock = z.infer<typeof flashcardsBlock>;
export type SummaryBlock = z.infer<typeof summaryBlock>;
export type LessonVisual = z.infer<typeof visualSchema>;


// ---------- Per-block semantic checks (zod v3 discriminatedUnion cannot hold refined objects) ----------
function refineQuestionBlock(q: QuestionBlock, ctx: z.RefinementCtx) {
  if ((q.kind === "mc" || q.kind === "multi") && (!q.options || q.options.length < 2)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${q.kind} question needs options` });
  }
  if (q.kind === "mc" && typeof q.answer !== "string") {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "mc answer must be an option id string" });
  }
  if (q.kind === "mc" && typeof q.answer === "string" && q.options && !q.options.some(o => o.id === q.answer)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: `mc answer "${q.answer}" is not an option id` });
  }
  if (q.kind === "multi" && !Array.isArray(q.answer)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "multi answer must be an array of option ids" });
  }
  if (q.kind === "multi" && Array.isArray(q.answer) && q.options) {
    for (const a of q.answer) {
      if (!q.options.some(o => o.id === a)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `multi answer "${a}" is not an option id` });
      }
    }
  }
  if (q.kind === "tf" && q.answer !== "true" && q.answer !== "false") {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'tf answer must be "true" or "false"' });
  }
  if (q.kind === "numeric" && typeof q.answer !== "number") {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "numeric answer must be a number" });
  }
  if (q.kind === "text" && typeof q.answer === "number") {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "text answer must be a string or array of accepted strings" });
  }
}

function refineSortBlock(b: SortBlock, ctx: z.RefinementCtx) {
  const binIds = new Set(b.bins.map(x => x.id));
  for (const it of b.items) {
    if (!binIds.has(it.bin)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `sort item "${it.text}" points to unknown bin "${it.bin}"` });
    }
  }
}

function refineClozeBlock(b: ClozeBlock, ctx: z.RefinementCtx) {
  const blanks = b.text.match(/\[\[([^\]]+)\]\]/g) || [];
  if (blanks.length < 1) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "cloze text needs at least one [[blank]]" });
  if (blanks.length > 6) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "cloze text has too many blanks (max 6)" });
}

function refineTapWordBlock(b: TapWordBlock, ctx: z.RefinementCtx) {
  const words = b.sentence.split(/\s+/).map(w => w.replace(/[^\p{L}\p{N}'’-]/gu, "").toLowerCase());
  for (const t of b.targets) {
    const norm = t.replace(/[^\p{L}\p{N}'’-]/gu, "").toLowerCase();
    if (!words.includes(norm)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `tapword target "${t}" is not a word in the sentence` });
    }
  }
}

function refineNumberLineBlock(b: NumberLineBlock, ctx: z.RefinementCtx) {
  if (b.max <= b.min) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "numberline max must be greater than min" });
  if (b.target < b.min || b.target > b.max) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "numberline target must be within [min, max]" });
  if ((b.max - b.min) / b.step > 200) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "numberline has too many steps (max 200)" });
}

function refineFractionBlock(b: FractionBlock, ctx: z.RefinementCtx) {
  if (b.target > b.parts) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "fraction target cannot exceed parts" });
}

export function refineBlock(block: LessonBlock, ctx: z.RefinementCtx) {
  switch (block.type) {
    case "question": return refineQuestionBlock(block, ctx);
    case "sort": return refineSortBlock(block, ctx);
    case "cloze": return refineClozeBlock(block, ctx);
    case "tapword": return refineTapWordBlock(block, ctx);
    case "numberline": return refineNumberLineBlock(block, ctx);
    case "fraction": return refineFractionBlock(block, ctx);
    default: return;
  }
}

/** Validate a single block including its semantic checks. */
export const singleBlockSchema = lessonBlockSchema.superRefine((b, ctx) => refineBlock(b, ctx));
export const CHECK_BLOCK_TYPES: ReadonlySet<LessonBlockType> = new Set([
  "question", "sort", "order", "match", "cloze", "tapword", "numberline", "fraction",
]);

export function isCheckBlock(block: LessonBlock): boolean {
  // A simulation is explanatory when the student is just exploring, and scored when the author
  // set a challenge to hit. It is the one block whose type does not settle the question.
  if (block.type === "simulation") return !!(block as { challenge?: unknown }).challenge;
  return CHECK_BLOCK_TYPES.has(block.type);
}

export function countChecks(blocks: LessonBlock[]): number {
  return blocks.filter(isCheckBlock).length;
}

/** Whole-lesson block list validation: unique ids, sensible structure. */
export const lessonBlocksSchema = z.array(lessonBlockSchema).min(3).max(40).superRefine((blocks, ctx) => {
  const seen = new Set<string>();
  blocks.forEach((b, i) => {
    if (seen.has(b.id)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `duplicate block id "${b.id}" at index ${i}` });
    }
    seen.add(b.id);
    const scoped: z.RefinementCtx = {
      path: [...ctx.path, i],
      addIssue: (issue) => ctx.addIssue({ ...issue, path: [i, ...(issue.path || [])] }),
    } as z.RefinementCtx;
    refineBlock(b, scoped);
  });
  if (blocks[0].type !== "intro") {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "first block must be an intro" });
  }
  if (countChecks(blocks) < 1) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "lesson needs at least one check block" });
  }
});

/** Shape used by the seed files, the AI generator and the admin JSON editor. */
export const lessonDocumentSchema = z.object({
  title: z.string().min(1).max(160),
  slug: z.string().min(1).max(160).regex(/^[a-z0-9-]+$/, "slug must be lowercase letters, numbers and dashes").optional(),
  grade: z.number().int().min(0).max(13), // 0 = any grade (interest courses)
  subject: z.string().min(1).max(80),
  topic: z.string().min(1).max(160),
  description: z.string().min(1).max(500),
  emoji: z.string().max(8).optional(),
  estimatedMinutes: z.number().int().min(1).max(60).default(8),
  xpReward: z.number().int().min(0).max(1000).default(150),
  sortOrder: z.number().int().default(0),
  tags: z.array(z.string().max(40)).max(10).default([]),
  blocks: lessonBlocksSchema,
});

export type LessonDocument = z.infer<typeof lessonDocumentSchema>;

export function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 140);
}

/**
 * Normalise a student's free text/number answer for comparison.
 * Shared by the web player and the mobile API so scoring is identical.
 */
export function normalizeAnswerText(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ").replace(/[.,!?;:"'’]/g, "");
}

export function gradeQuestionBlock(block: QuestionBlock, response: unknown): boolean {
  switch (block.kind) {
    case "mc":
    case "tf":
      return typeof response === "string" && response === block.answer;
    case "multi": {
      if (!Array.isArray(response) || !Array.isArray(block.answer)) return false;
      const a = [...response].map(String).sort().join("|");
      const b = [...block.answer].map(String).sort().join("|");
      return a === b;
    }
    case "numeric": {
      const n = typeof response === "number" ? response : parseFloat(String(response).replace(/[^0-9.-]/g, ""));
      if (Number.isNaN(n) || typeof block.answer !== "number") return false;
      const tol = block.tolerance ?? 0.0001;
      return Math.abs(n - block.answer) <= tol;
    }
    case "text": {
      const given = normalizeAnswerText(String(response ?? ""));
      const accepted = Array.isArray(block.answer) ? block.answer : [String(block.answer)];
      return accepted.some(a => normalizeAnswerText(a) === given);
    }
  }
}
